import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import type { ReactNode } from "react";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import PedalBikeIcon from "@mui/icons-material/PedalBike";
import TrainIcon from "@mui/icons-material/Train";
import { LatLng, TripLegMode } from "types/map";
import { CarState } from "types/cars";
import { ElectricGridCountry, tripEmission } from "libraries/emission";
import { greatCircleDistanceKm } from "libraries/flight";
import { hopEmissionKg } from "libraries/legEmissions";

type Props = {
  mode: TripLegMode;
  onChange: (mode: TripLegMode) => void;
  from?: LatLng | null;
  to?: LatLng | null;
  car?: CarState | null;
  gridCountry?: ElectricGridCountry;
};

const OPTIONS: Array<{
  value: TripLegMode;
  label: string;
  icon: ReactNode;
  basis: string;
}> = [
  {
    value: "drive",
    label: "Drive",
    icon: <DirectionsCarIcon sx={{ fontSize: 15 }} />,
    basis: "Selected vehicle emissions for this leg distance.",
  },
  {
    value: "walk",
    label: "Walk",
    icon: <DirectionsWalkIcon sx={{ fontSize: 15 }} />,
    basis:
      "CO₂ avoided vs driving this distance with the selected vehicle (negative = savings).",
  },
  {
    value: "bike",
    label: "Bike",
    icon: <PedalBikeIcon sx={{ fontSize: 15 }} />,
    basis:
      "CO₂ avoided vs driving this distance with the selected vehicle (negative = savings).",
  },
  {
    value: "train",
    label: "Transit",
    icon: <TrainIcon sx={{ fontSize: 15 }} />,
    basis: "~12.8 g CO₂/km (ADEME-style rail average).",
  },
  {
    value: "fly",
    label: "Fly",
    icon: <FlightTakeoffIcon sx={{ fontSize: 15 }} />,
    basis:
      "Short-haul ~0.246 kg CO₂/km (ADEME-style), including a 9% path detour.",
  },
];

const formatModeKg = (mode: TripLegMode, kg: number): string => {
  if (mode === "walk" || mode === "bike") {
    return `${kg.toFixed(1)} kg CO₂`;
  }
  return `~${kg.toFixed(1)} kg CO₂`;
};

const modeEmissionKg = (
  mode: TripLegMode,
  from: LatLng,
  to: LatLng,
  car: CarState | null | undefined,
  gridCountry: ElectricGridCountry | undefined
): number | undefined => {
  if (mode === "drive") {
    const meters = greatCircleDistanceKm(from, to) * 1000;
    const { totalKgEmission } = tripEmission(car ?? null, meters, {
      gridCountry: gridCountry ?? "norway",
    });
    return totalKgEmission;
  }

  return hopEmissionKg(mode, from, to, {
    car: car ?? null,
    gridCountry: gridCountry ?? "norway",
  });
};

const modeTooltipTitle = (
  label: string,
  basis: string,
  mode: TripLegMode,
  from?: LatLng | null,
  to?: LatLng | null,
  car?: CarState | null,
  gridCountry?: ElectricGridCountry
): string => {
  if (!from || !to) {
    return `${label}\n${basis}`;
  }

  const kg = modeEmissionKg(mode, from, to, car, gridCountry);
  if (kg === undefined) {
    return `${label}\n${basis}`;
  }

  return `${label} · ${formatModeKg(mode, kg)}\n${basis}`;
};

const TripLegModeToggle = ({
  mode,
  onChange,
  from,
  to,
  car,
  gridCountry,
}: Props) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={mode}
    onChange={(_, nextMode: TripLegMode | null) => {
      if (nextMode) {
        onChange(nextMode);
      }
    }}
    onMouseDown={(event) => event.stopPropagation()}
    onTouchStart={(event) => event.stopPropagation()}
    sx={{
      display: "flex",
      width: "100%",
      flexWrap: "nowrap",
      "& > *": {
        flex: 1,
        minWidth: 0,
      },
      "& .MuiToggleButton-root": {
        width: "100%",
        px: 0.25,
        py: 0.15,
        minWidth: 0,
        textTransform: "none",
        borderColor: "divider",
      },
    }}
  >
    {OPTIONS.map((option) => (
      <Tooltip
        key={option.value}
        title={modeTooltipTitle(
          option.label,
          option.basis,
          option.value,
          from,
          to,
          car,
          gridCountry
        )}
        placement="top"
        slotProps={{
          tooltip: {
            sx: { whiteSpace: "pre-line" },
          },
        }}
      >
        <ToggleButton
          value={option.value}
          aria-label={`${option.label} to this stop`}
        >
          {option.icon}
        </ToggleButton>
      </Tooltip>
    ))}
  </ToggleButtonGroup>
);

export default TripLegModeToggle;
